'use client';

// ── Minimum Trading Days · "Example" (Challenge week × Funded week) ───────────
// Figma "Moldura · Exemplo · dias": desktop 12071:59974 (792×552), mobile
// 12471:9566 (366×720). Every size is Figma px / 8 in rem. Two bordered week
// cards (chip + note, five day cells, status bar) and a legend. The 16px Carbon
// glyphs are the Figma exports in /public/general-rules-v3/minimum-trading-days/
// (multiply rect stripped). Values are examples; the page note says so.

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ExampleLabel, FigureFrame } from '../primitives';

const ASSETS = '/general-rules-v3/minimum-trading-days';
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const TEXT = 'mb-0 whitespace-nowrap text-[2rem] leading-[3rem]';

function Icon({ src }: { src: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={`${ASSETS}/${src}`} alt="" aria-hidden draggable={false} className="block h-[2rem] w-[2rem] max-w-none shrink-0" />;
}

function Week({
  chip,
  chipTone,
  note,
  traded,
  status,
  statusTone,
  'data-ui': dataUi,
}: {
  chip: string;
  chipTone: 'green' | 'neutral';
  note: ReactNode;
  traded: boolean[];
  status: string;
  statusTone: 'green' | 'red';
  'data-ui': string;
}) {
  return (
    <div data-ui={dataUi} className="flex w-full flex-col gap-[1.5rem] rounded-[0.25rem] border border-black/10 p-[2rem]">
      {/* Topo */}
      <div className="flex w-full flex-col lg:flex-row lg:items-center lg:justify-between">
        <div
          className={cn(
            'inline-flex w-fit items-center rounded-[0.25rem] px-[1.25rem] py-[0.25rem]',
            chipTone === 'green' ? 'bg-[#e2f3ef] text-[#196551]' : 'bg-[#f2f4f3] text-black',
          )}
        >
          <p className={`${TEXT} font-medium`}>{chip}</p>
        </div>
        <p className="mb-0 text-[2rem] leading-[3rem] text-black/40 lg:whitespace-nowrap">{note}</p>
      </div>

      {/* Dias */}
      <div className="flex w-full gap-[0.75rem] lg:gap-[1rem]">
        {DAYS.map((day, i) => {
          const on = traded[i];
          return (
            <div
              key={day}
              className={cn(
                'flex min-h-[9rem] min-w-0 flex-1 flex-col items-center justify-center gap-[0.75rem] rounded-[0.25rem] py-[1.5rem]',
                on ? 'border border-transparent bg-[#228e71]' : 'border border-black/10',
              )}
            >
              <p className={cn(`${TEXT} font-medium`, on ? 'text-white/80' : 'text-black/40')}>{day}</p>
              {on ? (
                <Icon src="check-white.svg" />
              ) : (
                <p className="mb-0 whitespace-nowrap text-[2rem] font-normal leading-[2rem] text-black/[0.24]">—</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Status */}
      <div
        className={cn(
          'flex w-full items-center gap-[1rem] rounded-[0.25rem] px-[1.5rem] py-[1rem]',
          statusTone === 'green' ? 'bg-[#e2f3ef] text-[#196551]' : 'bg-[#ac0003]/[0.08] text-[#ac0003]',
        )}
      >
        <Icon src={statusTone === 'green' ? 'check-green.svg' : 'warning-red.svg'} />
        <p className="mb-0 min-w-0 flex-1 text-[2rem] font-medium leading-[3rem]">{status}</p>
      </div>
    </div>
  );
}

export default function MinimumTradingDays() {
  return (
    <FigureFrame data-ui="figure.minimumTradingDays" className="gap-[2.5rem] font-geist">
      <ExampleLabel>EXAMPLE</ExampleLabel>

      <Week
        data-ui="figure.minimumTradingDays.week-challenge"
        chip="Challenge"
        chipTone="green"
        note="example: min. 3 days · to pass"
        traded={[true, false, true, true, false]}
        status="3 / 3 days · can pass once the target is reached"
        statusTone="green"
      />

      <Week
        data-ui="figure.minimumTradingDays.week-funded"
        chip="Funded"
        chipTone="neutral"
        note={
          <>
            example: min. 5 days ·{' '}
            <br className="lg:hidden" />
            before a payout
          </>
        }
        traded={[true, true, false, true, true]}
        status="4 / 5 days · 1 more trading day needed"
        statusTone="red"
      />

      {/* Legenda */}
      <div data-ui="figure.minimumTradingDays.legend" className="flex w-full flex-col gap-[3rem] lg:flex-row lg:items-start">
        <div className="flex items-center gap-[1rem]">
          <span className="h-[1.25rem] w-[1.25rem] shrink-0 rounded-[0.25rem] bg-[#228e71]" />
          <p className={`${TEXT} font-normal text-black/[0.64]`}>Traded (at least one trade placed)</p>
        </div>
        <div className="flex items-center gap-[1rem]">
          <span className="h-[1.25rem] w-[1.25rem] shrink-0 rounded-[0.25rem] border border-black/[0.24]" />
          <p className={`${TEXT} font-normal text-black/[0.64]`}>No trade — does not count</p>
        </div>
      </div>
    </FigureFrame>
  );
}
